import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const CurrencyContext = createContext({
  currency: "EUR",
  setCurrency: () => {},
  rates: { USD: 1.0 },
  format: (usd) => `$${Number(usd || 0).toFixed(2)}`,
  supported: ["USD"],
  loading: true,
});

const STORAGE_KEY = "rentalcar_currency";
const DEFAULT_CURRENCY = "EUR";

// Conservative offline fallback (rates against EUR) so the app is usable before
// /api/fx/rates resolves. The `Money` component receives EUR amounts and converts
// to the visitor's chosen display currency.
const FALLBACK_RATES = {
  EUR: 1.0, USD: 1.08, GBP: 0.84, CAD: 1.47, AUD: 1.65,
  INR: 90.0, AED: 3.97, JPY: 165.0, CHF: 0.95, SGD: 1.45,
};

const FALLBACK_SUPPORTED = ["EUR", "USD", "GBP", "CAD", "AUD", "INR", "AED", "JPY", "CHF", "SGD"];

const SYMBOL = {
  EUR: "€", USD: "$", GBP: "£", CAD: "CA$", AUD: "A$",
  INR: "₹", AED: "AED ", JPY: "¥", CHF: "CHF ", SGD: "S$",
};

/** Build a formatter that turns an EUR amount into a string in the chosen currency. */
function makeFormatter(currency, rates) {
  const rate = (rates && rates[currency]) || FALLBACK_RATES[currency] || 1;
  const symbol = SYMBOL[currency] || `${currency} `;
  // Currencies typically displayed without decimals
  const noDecimals = currency === "JPY" || currency === "INR";
  const fractionDigits = noDecimals ? 0 : 2;
  return (eurAmount) => {
    const n = Number(eurAmount || 0) * rate;
    const formatted = n.toLocaleString(undefined, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
    return `${symbol}${formatted}`;
  };
}

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || DEFAULT_CURRENCY; }
    catch { return DEFAULT_CURRENCY; }
  });
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [supported, setSupported] = useState(FALLBACK_SUPPORTED);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/fx/rates")
      .then((r) => {
        if (cancelled) return;
        if (r.data?.rates) setRates(r.data.rates);
        if (Array.isArray(r.data?.supported) && r.data.supported.length) setSupported(r.data.supported);
      })
      .catch((e) => console.error("fx rates load failed:", e))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const setCurrency = (next) => {
    setCurrencyState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch (e) { console.error("currency persist failed:", e); }
  };

  const format = useMemo(() => makeFormatter(currency, rates), [currency, rates]);

  const value = useMemo(
    () => ({ currency, setCurrency, rates, format, supported, loading }),
    [currency, rates, format, supported, loading]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export const useCurrency = () => useContext(CurrencyContext);
